import { useMemo, useState } from "react";
import { movies } from "@/mocks/catalog";
import type { MovieGenre } from "@/mocks/catalog";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";

type FilterKey = "全部" | MovieGenre;

const filters: FilterKey[] = ["全部", "动作片", "爱情片", "科幻片", "恐怖片", "喜剧片", "电视剧"];

export default function MovieExplorer() {
  const [active, setActive] = useState<FilterKey>("全部");
  const [keyword, setKeyword] = useState("");

  const result = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return movies.filter((movie) => {
      const matchGenre = active === "全部" || movie.genre === active;
      const matchKeyword =
        kw.length === 0 ||
        movie.title.toLowerCase().includes(kw) ||
        movie.director.toLowerCase().includes(kw) ||
        movie.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchGenre && matchKeyword;
    });
  }, [active, keyword]);

  return (
    <section id="explorer" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="片库 · 类型筛选"
          title="按类型即时筛选本页片库"
          description="选择一个类型，或输入片名、导演、标签关键词，下方条目会立刻更新。筛选只作用于本页已收录的内容，无需跳转与刷新。"
        />

        <div className="mt-8 flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {filters.map((item) => {
                const isActive = item === active;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setActive(item)}
                    aria-pressed={isActive}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary-500 font-medium text-background-50"
                        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    {item}
                  </button>
                );
              })}
            </div>

            <label className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 lg:w-72">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <span className="sr-only">按关键词筛选片库</span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索片名 / 导演 / 标签"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空搜索关键词"
                  className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </label>
          </div>

          <p className="text-xs text-foreground-500" aria-live="polite">
            当前筛选：<span className="font-medium text-foreground-800">{active}</span>
            {keyword.trim() && (
              <>
                {" · 关键词 "}
                <span className="font-medium text-foreground-800">「{keyword.trim()}」</span>
              </>
            )}
            {" · 共 "}
            <span className="font-medium text-primary-700">{result.length}</span> 条
          </p>
        </div>

        {result.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {result.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-unhappy-line text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-600">没有找到符合条件的条目，换个类型或关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setActive("全部");
                setKeyword("");
              }}
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}